import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";
import { useTranslation } from "react-i18next";

const CouponTable = ({
  coupons,
  selectedCoupons,
  onSelectCoupon,
  onSelectAll,
  onSort,
  sortConfig,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleStatus,
}) => {
  const { t } = useTranslation();
  const [hoveredRow, setHoveredRow] = useState(null);

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: "bg-success text-success-foreground",
        label: t("active"),
      },
      inactive: {
        color: "bg-secondary text-secondary-foreground",
        label: t("inactive"),
      },
      expired: { color: "bg-error text-error-foreground", label: t("expired") },
      scheduled: {
        color: "bg-warning text-warning-foreground",
        label: t("scheduled"),
      },
    };

    const config = statusConfig?.[status] || statusConfig?.inactive;
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}
      >
        {config?.label}
      </span>
    );
  };

  const getDiscountDisplay = (coupon) => {
    switch (coupon?.type) {
      case "ratio":
        return `${coupon?.value}%`;
      case "fixed":
        return `$${coupon?.value}`;
      case "bogo":
        return "BOGO";
      case "shipping":
        return "Free Ship";
      default:
        return coupon?.value;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) {
      return (
        <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />
      );
    }
    return sortConfig?.direction === "asc" ? (
      <Icon name="ArrowUp" size={14} className="text-foreground" />
    ) : (
      <Icon name="ArrowDown" size={14} className="text-foreground" />
    );
  };

  const handleSort = (column) => {
    onSort(column);
  };

  const isAllSelected =
    coupons?.length > 0 && selectedCoupons?.length === coupons?.length;
  const isIndeterminate =
    selectedCoupons?.length > 0 && selectedCoupons?.length < coupons?.length;

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm text-gray-700">
          <thead className="bg-green-50 text-green-900 font-semibold">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort("code")}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>{t("coupon_code")}</span>
                </button>
              </th>

              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort("type")}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>{t("type")}</span>
                </button>
              </th>

              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort("value")}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>{t("discount")}</span>
                </button>
              </th>

              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort("start_date")}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>{t("valid_period")}</span>
                </button>
              </th>

              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort("number_of_uses_for_user")}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>{t("usage")}</span>
                </button>
              </th>

              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort("status")}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>{t("status")}</span>
                </button>
              </th>

              <th className="px-4 py-3 text-center w-32">
                <span className="text-sm font-medium text-foreground">
                  {t("actions")}
                </span>
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {coupons?.map((coupon) => (
              <tr
                key={coupon?.uuid}
                className="hover:bg-muted/30 transition-micro"
                onMouseEnter={() => setHoveredRow(coupon?.uuid)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td className="px-4 py-3">
                  <div className="font-mono text-sm font-medium text-foreground">
                    {coupon?.code}
                  </div>
                </td>

                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-foreground">
                    {t(coupon?.type)}
                  </div>
                </td>

                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-foreground">
                    {getDiscountDisplay(coupon)}
                  </div>
                  <div className="text-xs text-muted-foreground capitalize">
                    {t(coupon?.condition)}
                  </div>
                </td>

                <td className="px-4 py-3">
                  <div className="text-sm text-foreground">
                    {formatDate(coupon?.start_date)} -{" "}
                    {formatDate(coupon?.end_date)}
                  </div>
                </td>

                <td className="px-4 py-3">
                  <div className="text-sm text-foreground">
                    {coupon?.number_of_uses_for_user} /{" "}
                    {coupon?.usage_limit || "∞"}
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                    <div
                      className="bg-primary h-1.5 rounded-full transition-smooth"
                      style={{
                        width: coupon?.usage_limit
                          ? `${Math.min(
                              (coupon?.number_of_uses_for_user /
                                coupon?.usage_limit) *
                                100,
                              100
                            )}%`
                          : "0%",
                      }}
                    />
                  </div>
                </td>

                <td className="px-4 py-3">{getStatusBadge(coupon?.status)}</td>

                <td className="px-4 py-3">
                  <div className="flex items-center justify-center space-x-1">
                    <button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(coupon)}
                      className="h-8 w-8"
                    >
                      <Icon name="Edit" size={14} />
                    </button>

                    <button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDuplicate(coupon)}
                      className="h-8 w-8"
                    >
                      <Icon name="Copy" size={14} />
                    </button>

                    <button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(coupon)}
                      className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                      <Icon name="Trash2" size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {coupons?.map((coupon) => (
          <div key={coupon?.uuid} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start space-x-3">
                <Checkbox
                  checked={selectedCoupons?.includes(coupon?.uuid)}
                  onChange={(e) =>
                    onSelectCoupon(coupon?.uuid, e?.target?.checked)
                  }
                  className="mt-1"
                />
                <div>
                  <div className="font-mono text-sm font-medium text-foreground">
                    {coupon?.code}
                  </div>
                  <div className="text-sm font-medium text-foreground mt-1">
                    {coupon?.type}
                  </div>
                </div>
              </div>
              {getStatusBadge(coupon?.status)}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
              <div>
                <span className="text-muted-foreground">Discount:</span>
                <div className="font-medium text-foreground">
                  {getDiscountDisplay(coupon)}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Usage:</span>
                <div className="font-medium text-foreground">
                  {coupon?.number_of_uses_for_user} /{" "}
                  {coupon?.usage_limit || "∞"}
                </div>
              </div>
            </div>

            <div className="text-xs text-muted-foreground mb-3">
              Valid: {formatDate(coupon?.start_date)} -{" "}
              {formatDate(coupon?.end_date)}
            </div>

            <div className="flex items-center justify-end space-x-1">
              <button
                variant="ghost"
                size="sm"
                onClick={() => onToggleStatus(coupon)}
                iconName={coupon?.status === "active" ? "Pause" : "Play"}
                iconPosition="left"
              >
                {coupon?.status === "active" ? "Pause" : "Activate"}
              </button>

              <button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(coupon)}
                iconName="Edit"
              />

              <button
                variant="ghost"
                size="sm"
                onClick={() => onDuplicate(coupon)}
                iconName="Copy"
              />

              <button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(coupon)}
                iconName="Trash2"
                className="text-destructive hover:text-destructive"
              />
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {coupons?.length === 0 && (
        <div className="text-center py-12">
          <Icon
            name="Ticket"
            size={48}
            className="text-muted-foreground mx-auto mb-4"
          />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {t("no_cop")}
          </h3>
  
        </div>
      )}
    </>
  );
};

export default CouponTable;
